import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Globe, Lock, Sparkles, Crown } from "lucide-react";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";
import {
  bumpTrialUsed,
  classifyAiIntent,
  evaluateOracleAccess,
  loadGlobalOracleSettings,
  type GlobalOracleSettings,
  type OracleAccess,
} from "@/lib/ai/aiAccessModel";
import { resolvePlan, type ResolvedPlan } from "@/lib/billing/resolvePlan";
import type { ChatMessage } from "@/types";

export function GlobalSoccerOracle() {
  const { t } = useLanguage();
  const { authReady } = useAuth();
  const [plan, setPlan] = useState<ResolvedPlan | null>(null);
  const [settings, setSettings] = useState<GlobalOracleSettings | null>(null);
  const [access, setAccess] = useState<OracleAccess | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  useEffect(() => {
    if (!authReady) return;
    if (typeof console !== "undefined") console.log("[ORACLE_BOOTSTRAP_START]");
    void trackEvent("ai_oracle_opened", { channel: "global_oracle" });
    (async () => {
      const [p, s] = await Promise.all([resolvePlan({ silent: true }), loadGlobalOracleSettings()]);
      setPlan(p);
      setSettings(s);
      setAccess(evaluateOracleAccess(p.plan_code, s));
      if (typeof console !== "undefined") console.log("[ORACLE_BOOTSTRAP_READY]", { plan: p.plan_code });
    })();
  }, [authReady]);

  const greeting = useMemo<ChatMessage>(() => ({
    id: "go-greeting",
    role: "ai",
    content: t("global_oracle_greeting"),
    at: Date.now(),
  }), [t]);

  useEffect(() => {
    if (messages.length === 0) setMessages([greeting]);
  }, [greeting, messages.length]);

  const handleSend = (text: string) => {
    if (!access || !plan || !settings) return;
    const intent = classifyAiIntent(text);
    void trackEvent("global_oracle_question_submitted", { intent, plan: plan.plan_code });
    if (access.allowed && (access.trial_remaining ?? -1) >= 0) {
      const scope = access.is_premium ? plan.plan_code : "free";
      const used = bumpTrialUsed(scope);
      const remaining = Math.max(0, settings.trial_questions_per_day - used);
      void trackEvent("global_oracle_trial_used", { used, remaining, scope });
      const newRemaining = Math.max(0, (access.trial_remaining ?? 0) - 1);
      setAccess({ ...access, trial_remaining: newRemaining, allowed: newRemaining > 0 || access.is_premium });
    }
    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: "user", content: text, at: Date.now() };
    setMessages((m) => [...m, userMsg]);
  };

  const handleAiReply = (content: string) => {
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "ai", content, at: Date.now() }]);
    void trackEvent("ai_oracle_response_generated", { len: content.length, channel: "global_oracle" });
  };

  if (!plan || !settings || !access) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl glass text-xs text-muted-foreground">
        {t("loading")}
      </div>
    );
  }

  if (!access.allowed) {
    return <Paywall reason={access.reason} onUpgrade={() => void trackEvent("global_oracle_upgrade_clicked", { from: access.reason })} />;
  }

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-[oklch(0.45_0.18_280)] to-[oklch(0.6_0.22_320)] px-3 py-2 text-white">
        <Globe className="h-4 w-4" />
        <div className="text-xs font-bold">{t("global_oracle_title")}</div>
        {access.is_premium ? (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold">
            <Crown className="h-3 w-3" /> {t("global_oracle_badge_premium")}
          </span>
        ) : (
          <span className="ml-auto rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold">
            {t("global_oracle_trial_left", { n: access.trial_remaining })}
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0">
        <ChatPanel
          messages={messages}
          onSend={handleSend}
          onAiReply={handleAiReply}
          channel="global_oracle"
          maxWords={access.max_words}
          placeholder={t("global_oracle_placeholder")}
        />
      </div>
    </div>
  );
}

function Paywall({ reason, onUpgrade }: { reason: OracleAccess["reason"]; onUpgrade: () => void }) {
  const { t } = useLanguage();
  useEffect(() => {
    void trackEvent("global_oracle_access_denied", { reason });
  }, [reason]);
  const title = reason === "trial_exhausted" ? t("global_oracle_paywall_trial_done") : t("global_oracle_paywall_title");
  const desc = reason === "disabled" ? t("global_oracle_paywall_disabled") : t("global_oracle_paywall_desc");
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 rounded-2xl glass p-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[oklch(0.45_0.18_280)] to-[oklch(0.6_0.22_320)] text-white">
        <Lock className="h-5 w-5" />
      </div>
      <div className="text-sm font-bold">{title}</div>
      <p className="max-w-xs text-xs text-muted-foreground">{desc}</p>
      <ul className="text-[11px] text-muted-foreground space-y-1">
        <li>· {t("global_oracle_feat_history")}</li>
        <li>· {t("global_oracle_feat_leagues")}</li>
        <li>· {t("global_oracle_feat_compare")}</li>
      </ul>
      <a
        href="/mi-plan"
        onClick={onUpgrade}
        className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[oklch(0.45_0.18_280)] to-[oklch(0.6_0.22_320)] px-4 py-2 text-xs font-bold text-white shadow-lg"
      >
        <Sparkles className="h-3.5 w-3.5" /> {t("global_oracle_upgrade_cta")}
      </a>
    </div>
  );
}